import React, {createContext, useCallback, useContext, useMemo, useRef, useState} from "react";
import "./ToastContext.scss";

/**
 * Lightweight toast notifications — for quick "Saved" / "Updated" / "Deleted"
 * confirmations that shouldn't interrupt the user the way the full-screen
 * useLoading() splash does. Use useLoading() for "the page isn't ready yet"
 * (blocking); use useToast() for "that action just finished" (non-blocking).
 *
 *   const toast = useToast();
 *   toast.success("Post updated");
 *   toast.error("Couldn't save — try again");
 */

const ToastContext = createContext({
  success: () => {},
  error: () => {},
  show: () => {},
});

let idSeq = 0;

export const ToastProvider = ({children}) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback(id => {
    setToasts(list => list.filter(t => t.id !== id));
    clearTimeout(timers.current[id]);
    delete timers.current[id];
  }, []);

  const show = useCallback((message, type = "success", duration = 3200) => {
    const id = ++idSeq;
    setToasts(list => [...list, {id, message, type}]);
    timers.current[id] = setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  const value = useMemo(() => ({
    show,
    success: msg => show(msg, "success"),
    error: msg => show(msg, "error", 5000),
  }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="rd-toasts" aria-live="polite" aria-atomic="false">
        {toasts.map(t => (
          <div
            key={t.id}
            className={`rd-toast rd-toast--${t.type}`}
            role="status"
            onClick={() => dismiss(t.id)}
          >
            <span className="rd-toast__icon" aria-hidden="true">
              {t.type === "error" ? "✕" : "✓"}
            </span>
            <span className="rd-toast__msg">{t.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export function useToast() {
  return useContext(ToastContext);
}
